import React, { useEffect, useState } from "react";
import usePagination from "src/customHooks/usePagination";
import PageHeader from "src/components/pageHeader";
import Pagination from "src/components/pagination";
import { Navigate } from "react-router-dom";
import PageLoading from "src/components/pageLoading";
import { useCountPostsQuery, useGetPostsQuery } from "../post.service";
import { PostsTable } from "../components/posts/table";
import { PostsSearch } from "../components/posts/search";
import { PostsTableSkeleton } from "../components/posts/PostsTableSkeleton";
import { FilterPostsInputs, PostWithUser } from "../types/index.type";
import { LinkButton } from "src/components/LinkButton";
import { FaArrowUp, FaPlus } from "react-icons/fa";
import { PostsTabs } from "../components/posts/tabs";
import { ScheduledPosts } from "../components/scheduled-posts";
import { ScheduledPost } from "../types/scheduled-posts.entity";

const Posts = () => {
  const [isScheduledView, setIsScheduledView] = useState<boolean>(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const { data, numberOfPages, currentPage, next, prev, isSuccess, isError, updateQuery, isLoading, isFetching } =
    usePagination<PostWithUser>({
      useGetDataQuery: useGetPostsQuery,
      useCountData: useCountPostsQuery,
    });

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleTabChange = (isScheduled: boolean) => {
    setIsScheduledView(isScheduled);
    updateQuery({ isScheduled });
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Validate data type matches the current view
  const isDataValid = () => {
    if (!data || data.length === 0) return true; // Empty is always valid

    const hasScheduledDate = "scheduledDate" in data[0];
    return hasScheduledDate === isScheduledView;
  };

  const shouldShowLoading = isLoading || !isDataValid();

  return (
    <div className="min-h-screen bg-admin-content p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <PageHeader text="Posts Management" />

        <div className="flex justify-center items-center mt-4">
          <LinkButton to="/feed/create" text="Add New Post" icon={FaPlus} />
        </div>

        <PostsTabs updateFilterQuery={handleTabChange} />

        <div className="bg-card border border-border rounded-lg">
          <PostsSearch updateFilterQuery={(where: FilterPostsInputs) => updateQuery(where)} />
        </div>

        {shouldShowLoading || isLoading || isFetching ? (
          <>
            <PostsTableSkeleton rows={6} />
            <Pagination numberOfPages={numberOfPages || 1} currentPage={currentPage} prev={prev} next={next} isLoading={isLoading || isFetching} />
          </>
        ) : isSuccess && data.length > 0 ? (
          <div className="space-y-4">
            {isScheduledView ? (
              <ScheduledPosts posts={data as unknown as ScheduledPost[]} />
            ) : (
              <PostsTable data={data} />
            )}

            <Pagination numberOfPages={numberOfPages || 1} currentPage={currentPage} prev={prev} next={next} isLoading={isLoading || isFetching} />
          </div>
        ) : isSuccess && data.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No {isScheduledView ? "scheduled " : ""}posts found
          </div>
        ) : isError ? (
          <Navigate to="/404" />
        ) : (
          <PageLoading />
        )}
      </div>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-emerald-600 hover:bg-emerald-700 text-white p-1.5 rounded-full shadow-2xl ring-4 ring-amber-300/80 ring-offset-2 ring-offset-white transition-all duration-300 z-50 group"
          aria-label="Scroll to top"
        >
          <div className="flex items-center justify-center rounded-full border-2 border-amber-100/80 px-3 py-3">
            <FaArrowUp className="text-xl group-hover:-translate-y-1 transition-transform duration-300" />
          </div>
        </button>
      )}
    </div>
  );
};

export default Posts;
